console.log('[TTCart] Loading');

window.TTCart = (() => {
  async function fetchCart() {
    const res = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!res.ok) {
      throw new Error('Failed to fetch cart');
    }

    return await res.json();
  }

  function isSellingPlanItem(item) {
    return Boolean(
      item.selling_plan_allocation ||
      item.selling_plan_id ||
      item.properties?._selling_plan ||
      item.properties?.selling_plan
    );
  }

  function getLineItemByVariantId(cart, variantId) {
    return (
      (cart.items || []).find(
        (item) =>
          Number(item.variant_id) === Number(variantId) &&
          !isSellingPlanItem(item)
      ) || null
    );
  }

  function getQuantityForVariant(cart, variantId) {
    return (cart.items || []).reduce((total, item) => {
      if (
        Number(item.variant_id) === Number(variantId) &&
        !isSellingPlanItem(item)
      ) {
        return total + item.quantity;
      }

      return total;
    }, 0);
  }

  function getBundledSections() {
    const detail = {
      sections: ['variant-added']
    };

    document.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        detail
      })
    );

    const sections = Array.from(new Set(detail.sections));

    console.log('[TTCart] Bundled sections:', sections);

    return sections;
  }

  async function addVariant(variantId, quantity, sections) {
    const res = await fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity
            }
          ],
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw data;
    }

    return data;
  }

  async function changeLine(line, quantity, sections) {
    const res = await fetch(
      `${window.Shopify.routes.root}cart/change.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw data;
    }

    return data;
  }

  async function changeSellingPlan(
    lineKey,
    sellingPlanId,
    variantId,
    quantity
  ) {
    console.group('[TTCart] Change selling plan');
    console.log('Line key:', lineKey);
    console.log('Selling plan ID:', sellingPlanId);
    console.log('Variant ID:', variantId);
    console.log('Quantity:', quantity);

    try {
      const sections = getBundledSections();

      const payload = {
        id: lineKey,
        quantity: Math.max(1, Number(quantity) || 1),
        selling_plan: Number(sellingPlanId),
        sections: sections.join(','),
        sections_url: window.location.pathname
      };

      console.log('Request payload:', payload);

      const res = await fetch(
        `${window.Shopify.routes.root}cart/change.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      console.log(
        '/cart/change.js response status:',
        res.status,
        res.statusText
      );

      const sectionResponse = await res.json();

      console.log(
        '/cart/change.js response:',
        sectionResponse
      );

      if (!res.ok) {
        throw sectionResponse;
      }

      const updatedCart = await fetchCart();

      const updatedItem = updatedCart.items.find((item) => {
        const itemPlanId =
          item.selling_plan_allocation?.selling_plan?.id;

        return (
          Number(item.variant_id) === Number(variantId) &&
          String(itemPlanId) === String(sellingPlanId)
        );
      });

      console.log('Updated subscription item:', updatedItem);

      console.table(
        updatedCart.items.map((item) => ({
          title: item.product_title,
          key: item.key,
          variantId: item.variant_id,
          sellingPlanId:
            item.selling_plan_allocation?.selling_plan?.id ||
            null,
          sellingPlanName:
            item.selling_plan_allocation?.selling_plan?.name ||
            null,
          quantity: item.quantity
        }))
      );

      if (!updatedItem) {
        throw new Error(
          'The updated selling plan was not found in the cart.'
        );
      }

      if (
        Number(updatedItem.quantity) !==
        Math.max(1, Number(quantity) || 1)
      ) {
        throw new Error(
          'The selling plan changed, but the quantity was not preserved.'
        );
      }

      updatedCart.sections =
        sectionResponse?.sections || {};

      console.log(
        'Returned section names:',
        Object.keys(updatedCart.sections)
      );

      document.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'cart:change',
            cart: updatedCart
          }
        })
      );

      console.log('cart:change dispatched');

      return updatedCart;
    } catch (error) {
      console.error(
        '[TTCart] Selling-plan change failed:',
        error
      );

      throw error;
    } finally {
      console.groupEnd();
    }
  }

  async function setVariantQuantity(
    variantId,
    targetQuantity
  ) {
    const quantity = Math.max(
      0,
      Number(targetQuantity) || 0
    );

    const existingCart = await fetchCart();

    const existingItem = getLineItemByVariantId(
      existingCart,
      variantId
    );

    const sections = getBundledSections();

    let sectionResponse = null;

    if (!existingItem && quantity > 0) {
      sectionResponse = await addVariant(
        variantId,
        quantity,
        sections
      );
    } else if (existingItem) {
      const lineIndex =
        existingCart.items.findIndex(
          (item) =>
            Number(item.variant_id) ===
              Number(variantId) &&
            !isSellingPlanItem(item)
        ) + 1;

      sectionResponse = await changeLine(
        lineIndex,
        quantity,
        sections
      );
    } else {
      sectionResponse = {
        sections: {}
      };
    }

    const updatedCart = await fetchCart();

    updatedCart.sections =
      sectionResponse?.sections || {};

    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'cart:change',
          cart: updatedCart
        }
      })
    );

    return updatedCart;
  }

  return {
    fetchCart,
    getLineItemByVariantId,
    getQuantityForVariant,
    setVariantQuantity,
    changeSellingPlan,
    isSellingPlanItem
  };
})();

console.log('[TTCart] Loaded:', window.TTCart);
console.log(
  '[TTCart] Installing selling-plan listener'
);

document.addEventListener(
  'change',
  async (event) => {
    const selector = event.target.closest?.(
      '[data-selling-plan-selector]'
    );

    if (!selector) {
      return;
    }

    const previousValue =
      selector.dataset.currentSellingPlan;

    const lineItem = selector.closest('line-item');
    const loader = lineItem?.querySelector('pill-loader');

    console.log(
      '[TTCart] Selling-plan selector changed:',
      {
        lineKey: selector.dataset.lineKey,
        variantId: selector.dataset.variantId,
        quantity: selector.dataset.quantity,
        previousValue,
        newValue: selector.value
      }
    );

    selector.disabled = true;
    selector.setAttribute('aria-busy', 'true');

    lineItem?.setAttribute(
      'data-selling-plan-loading',
      ''
    );

    loader?.setAttribute('aria-busy', 'true');

    try {
      await window.TTCart.changeSellingPlan(
        selector.dataset.lineKey,
        selector.value,
        selector.dataset.variantId,
        selector.dataset.quantity
      );

      selector.dataset.currentSellingPlan =
        selector.value;

      console.log(
        '[TTCart] Selling plan updated successfully'
      );
    } catch (error) {
      console.error(
        '[TTCart] Selling-plan update failed:',
        error
      );

      selector.value = previousValue;
    } finally {
      /*
       * If the drawer rerendered, these elements may no longer
       * be connected. If it did not, restore their usable state.
       */
      if (selector.isConnected) {
        selector.disabled = false;
        selector.removeAttribute('aria-busy');
      }

      if (lineItem?.isConnected) {
        lineItem.removeAttribute(
          'data-selling-plan-loading'
        );
      }

      if (loader?.isConnected) {
        loader.removeAttribute('aria-busy');
      }
    }
  },
  true
);